import { Form } from 'antd';
import type { CoverageAddress } from 'hydra-data-types';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Color } from '../../../config/Color';
import {
  fetchAddressSuggestions,
  fetchCoverageAddress,
  loadPlacesLibrary,
} from '../../../helpers/GooglePlaces';
import { recordError } from '../../../util/Errors';
import { FlowContentBody, QuestionWrapper } from '../../styles/Containers';
import { ErrorText } from '../../styles/ErrorMessage';
import { InfoBox, InfoBoxProps } from '../../styles/InfoBox';
import { SecondaryButton } from '../../styles/LayoutButtons';
import { TextInput } from '../../styles/TextInput';
import { BodySM, BodyXS, HeroTitle } from '../../styles/Typography';
import { LayoutActions } from './LayoutActions';

export type CoverageAddressLayoutConfig = {
  title: string;
  addressLabel: string;
  addressPlaceholder: string;
  notFoundButtonTitle: string;
  infoBox?: InfoBoxProps;
  primaryButtonTitle: string;
};

type Props = CoverageAddressLayoutConfig & {
  prefill?: CoverageAddress;
  onSubmit: (address: CoverageAddress) => void;
  onNotFound: () => void;
};

/** Google bills per request outside a session, so wait for a pause and a meaningful prefix. */
const DEBOUNCE_MS = 250;
const MIN_INPUT_LENGTH = 3;

const INCOMPLETE_ADDRESS_ERROR = 'Please choose a full street address from the list.';

export const CoverageAddressLayout: FunctionComponent<Props> = (props) => {
  const [places, setPlaces] = useState<google.maps.PlacesLibrary>();
  const [input, setInput] = useState(props.prefill?.formattedAddress ?? '');
  const [selected, setSelected] = useState<CoverageAddress | undefined>(props.prefill);
  const [predictions, setPredictions] = useState<google.maps.places.PlacePrediction[]>([]);
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState<string>();
  // One token spans a typing session up to the fetchFields call, then a new one is created lazily.
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken>();
  // Monotonic id so a slow response for an earlier query can't overwrite newer suggestions.
  const requestIdRef = useRef(0);

  useEffect(function loadPlaces() {
    let cancelled = false;
    void loadPlacesLibrary().then((library) => {
      if (!cancelled) setPlaces(library);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(
    function fetchSuggestionsAfterPause() {
      const query = input.trim();
      if (
        places === undefined ||
        selected !== undefined ||
        isResolving ||
        query.length < MIN_INPUT_LENGTH
      ) {
        setPredictions([]);
        return;
      }
      const requestId = ++requestIdRef.current;
      const timer = setTimeout(async () => {
        if (sessionTokenRef.current === undefined) {
          sessionTokenRef.current = new places.AutocompleteSessionToken();
        }
        try {
          const results = await fetchAddressSuggestions(places, query, sessionTokenRef.current);
          if (requestId === requestIdRef.current) setPredictions(results);
        } catch (fetchError) {
          recordError(fetchError);
        }
      }, DEBOUNCE_MS);
      return () => clearTimeout(timer);
    },
    [input, places, selected, isResolving]
  );

  function onInputChange(value: string): void {
    setInput(value);
    // Editing after a pick invalidates it; Next stays disabled until they choose again.
    setSelected(undefined);
    setError(undefined);
  }

  async function onSelectPrediction(prediction: google.maps.places.PlacePrediction): Promise<void> {
    requestIdRef.current += 1;
    setPredictions([]);
    setError(undefined);
    setIsResolving(true);
    setInput(prediction.text.text);
    try {
      const address = await fetchCoverageAddress(prediction);
      // fetchFields closes the billing session; the next keystroke starts a fresh one.
      sessionTokenRef.current = undefined;
      if (address === undefined) {
        setError(INCOMPLETE_ADDRESS_ERROR);
      } else {
        setSelected(address);
      }
    } catch (selectError) {
      recordError(selectError);
      setError(INCOMPLETE_ADDRESS_ERROR);
    } finally {
      setIsResolving(false);
    }
  }

  function onPrimaryPress(): void {
    if (selected !== undefined) props.onSubmit(selected);
  }

  return (
    <Form>
      <QuestionWrapper>
        <FlowContentBody>
          <HeroTitle data-test="surveyQuestion">{props.title}</HeroTitle>

          <FieldCard>
            <FieldLabel>
              {props.addressLabel}
              <RequiredMark aria-hidden="true">*</RequiredMark>
            </FieldLabel>
            <Form.Item>
              <TextInput
                type="text"
                autoComplete="off"
                value={input}
                placeholder={props.addressPlaceholder}
                aria-label={props.addressLabel}
                data-testid="coverage-address-input"
                onChange={({ target: { value } }) => onInputChange(value)}
              />
            </Form.Item>
            {predictions.length > 0 && (
              <SuggestionList role="listbox" aria-label={props.addressLabel}>
                {predictions.map((prediction) => (
                  <li key={prediction.placeId} role="option" aria-selected={false}>
                    <SuggestionButton
                      type="button"
                      data-testid="coverage-address-suggestion"
                      onClick={() => void onSelectPrediction(prediction)}
                    >
                      <SuggestionMainText>
                        {prediction.mainText?.text ?? prediction.text.text}
                      </SuggestionMainText>
                      {prediction.secondaryText !== null && (
                        <SuggestionSecondaryText>
                          {prediction.secondaryText.text}
                        </SuggestionSecondaryText>
                      )}
                    </SuggestionButton>
                  </li>
                ))}
              </SuggestionList>
            )}
          </FieldCard>

          {error !== undefined && (
            <ErrorText data-testid="coverage-address-error">{error}</ErrorText>
          )}

          <NotFoundRow>
            <SecondaryButton data-test="coverage-address-not-found" onClick={props.onNotFound}>
              {props.notFoundButtonTitle}
            </SecondaryButton>
          </NotFoundRow>

          {props.infoBox !== undefined && <InfoBox {...props.infoBox} />}
        </FlowContentBody>
        <LayoutActions
          primaryButtonTitle={props.primaryButtonTitle}
          onPressPrimaryButton={onPrimaryPress}
          primaryButtonDisabled={selected === undefined || isResolving}
        />
      </QuestionWrapper>
    </Form>
  );
};

const FieldCard = styled.div`
  background-color: ${Color.surface.muted};
  border-radius: 24px;
  padding: ${({ theme }) => theme.spacing._16px};
  margin-top: ${({ theme }) => theme.spacing._24px};
`;

const FieldLabel = styled(BodySM)`
  margin-bottom: ${({ theme }) => theme.spacing._8px};
`;

const RequiredMark = styled.span`
  color: ${Color.state.error};
  margin-left: ${({ theme }) => theme.spacing._4px};
`;

const SuggestionList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border-radius: 14px;
  overflow: hidden;
  background-color: ${Color.surface.onAccent};
`;

const SuggestionButton = styled.button`
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  border-bottom: 1px solid ${Color.flow.muted};
  padding: ${({ theme }) => `${theme.spacing._12px} ${theme.spacing._16px}`};
  cursor: pointer;
  &:hover,
  &:focus-visible {
    background-color: ${Color.flow.muted};
  }
`;

const SuggestionMainText = styled(BodySM)`
  margin: 0;
`;

const SuggestionSecondaryText = styled(BodyXS)`
  margin: 0;
  color: ${Color.content.muted};
`;

const NotFoundRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing._16px};
`;
